// src/pages/auth/SplashScreen.jsx

import { useEffect, useState } from "react";
import { deviceAPI, getDeviceId, storeCompany } from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";
import logo from "../../../assets/logo.png";

export default function SplashScreen({ onRegistered }) {
  const { loginWithCompany } = useAuth();

  const [status, setStatus] = useState("checking"); // checking | not-found | error
  const [errType, setErrType] = useState("");

  // 🔥 MAIN CHECK
  useEffect(() => {
    let cancelled = false;

    async function check() {
      try {
        const deviceId = await getDeviceId();
        const res = await deviceAPI.checkRegistration(deviceId);

        if (cancelled) return;

        if (res.status === "success" && res.data?.CM_Id) {
          storeCompany(res.data);
          loginWithCompany(res.data);
        } else {
          setStatus("not-found");
          setTimeout(() => onRegistered(), 800);
        }
      } catch (e) {
        if (cancelled) return;
        console.error("[SplashScreen]", e);

        // 🔥 SIMPLE ERROR TYPE (no technical UI)
        if (e.message?.includes("HTTP")) {
          setErrType("server");
        } else {
          setErrType("network");
        }

        setStatus("error");
      }
    }

    const t = setTimeout(check, 1000);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, []);

  // 🔄 RETRY
  const handleRetry = () => {
    setStatus("checking");
  };

  // 🔁 RE-RUN ON RETRY
  useEffect(() => {
    if (status !== "checking") return;

    let cancelled = false;

    async function check() {
      try {
        const deviceId = await getDeviceId();
        const res = await deviceAPI.checkRegistration(deviceId);

        if (cancelled) return;

        if (res.status === "success" && res.data?.CM_Id) {
          storeCompany(res.data);
          loginWithCompany(res.data);
        } else {
          setStatus("not-found");
          setTimeout(() => onRegistered(), 800);
        }
      } catch (e) {
        if (cancelled) return;

        if (e.message?.includes("HTTP")) {
          setErrType("server");
        } else {
          setErrType("network");
        }

        setStatus("error");
      }
    }

    const t = setTimeout(check, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [status]);

  return (
    <div style={styles.page}>
      <div style={styles.inner}>
        
        {/* LOGO */}
        <div style={styles.logoWrap}>
          <img src={logo} style={styles.logoIcon} alt="logo" />
          <h1 style={styles.logoTitle}>TailorExpert</h1>
          <p style={styles.logoSub}>Tailoring Order Management System</p>
        </div>

        {/* STATUS */}
        <div style={styles.statusArea}>

          {/* LOADING */}
          {status === "checking" && (
            <>
              <div style={styles.spinner} />
              <p style={styles.statusText}>Starting application…</p>
            </>
          )}

          {/* SETUP */}
          {status === "not-found" && (
            <>
              <div style={styles.spinner} />
              <p style={styles.statusText}>Preparing your workspace…</p>
            </>
          )}

          {/* 🔥 CLEAN ERROR UI */}
          {status === "error" && (
            <div style={styles.errorBox}>
              <div style={styles.errorIcon}>❌</div>

              <p style={styles.errorTitle}>Something went wrong</p>

              <p style={styles.errorText}>
                {errType === "server"
                  ? "Server error. Please try again later."
                  : "Unable to connect. Please check your internet."}
              </p>

              <button style={styles.retryBtn} onClick={handleRetry}>
                🔄 Retry
              </button>
            </div>
          )}
        </div>

        <p style={styles.footer}>Tailor Expert v1.0</p>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background: "var(--bg)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  inner: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 32,
    padding: "0 24px",
    textAlign: "center",
    maxWidth: 400,
    width: "100%",
  },

  logoWrap: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 10,
  },

  logoIcon: {
    width: 72,
    height: 72,
    borderRadius: 20,
    objectFit: "cover",
    boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
  },

  logoTitle: {
    fontSize: 28,
    fontWeight: 700,
    color: "var(--text)",
  },

  logoSub: {
    fontSize: 13,
    color: "var(--text-dim)",
  },

  statusArea: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 12,
  },

  spinner: {
    width: 32,
    height: 32,
    border: "3px solid var(--border)",
    borderTop: "3px solid var(--gold)",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
  },

  statusText: {
    fontSize: 14,
    color: "var(--text-muted)",
  },

  errorBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 10,
  },

  errorIcon: {
    fontSize: 36,
  },

  errorTitle: {
    fontSize: 18,
    fontWeight: 700,
    color: "var(--red)",
  },

  errorText: {
    fontSize: 13,
    color: "var(--text-muted)",
    maxWidth: 260,
  },

  retryBtn: {
    marginTop: 6,
    padding: "10px 26px",
    background: "var(--gold)",
    color: "var(--bg)",
    border: "none",
    borderRadius: 8,
    fontWeight: 600,
    cursor: "pointer",
  },

  footer: {
    position: "fixed",
    bottom: 20,
    fontSize: 11,
    color: "var(--text-dim)",
  },
};

// spinner animation
if (typeof document !== "undefined" && !document.getElementById("spin-style")) {
  const s = document.createElement("style");
  s.id = "spin-style";
  s.textContent = `@keyframes spin { to { transform: rotate(360deg); } }`;
  document.head.appendChild(s);
}